import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  searchHistoryService,
  type SearchHistoryItem,
} from "../services/SearchHistoryService";
import {
  useSAPSuggestions,
  type SuggestionItem,
} from "../hooks/useSAPSuggestions";
import { useSAPDataStore } from "../store/SAPDataStore";
import { Icon, useModKey } from "@digitalisation/enlixy-comp";
import { searchStyles, cx } from "../styles";
import { useThemeClasses } from "../providers/ThemeProvider";
import { useTheme } from "../providers/ThemeProvider";

interface SearchFieldProps {
  className?: string;
  onSearch?: (query: string) => void;
  onExpandedChange?: (expanded: boolean) => void;
}

const SearchField: React.FC<SearchFieldProps> = ({
  className = "",
  onSearch,
  onExpandedChange,
}) => {
  const navigate = useNavigate();
  const themeClasses = useThemeClasses();
  const { currentTheme } = useTheme();
  const {
    machines,
    lines,
    searchTags,
    setSearchTags,
    searchOperators,
    setSearchOperator,
  } = useSAPDataStore();
  // Search is always expanded now — no collapse/toggle. setter is a no-op so the
  // existing collapse call-sites keep the field open.
  const searchExpanded = true;
  const setSearchExpanded = (_expanded?: boolean) => {};
  const [searchValue, setSearchValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  // Notify parent when expanded state changes
  useEffect(() => {
    onExpandedChange?.(searchExpanded);
  }, [searchExpanded, onExpandedChange]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState<SearchHistoryItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  // Field is always expanded now, so it never enters an "expanding" transition.
  const isExpanding = false;
  const [isAddingTag, setIsAddingTag] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  // Only get SAP suggestions when input is focused and there's a search value
  const suggestions = useSAPSuggestions(
    isInputFocused && searchValue.length >= 2 ? searchValue : "",
    12,
  );

  // Load recent searches when component mounts
  useEffect(() => {
    setRecentSearches(searchHistoryService.getSearchHistory());
  }, []);

  // Ensure search field stays expanded when there are tags
  useEffect(() => {
    if (searchTags.length > 0 && !searchExpanded) {
      setSearchExpanded(true);
    }
  }, [searchTags.length, searchExpanded]);

  // Show dropdown when input is focused and there's content to show
  useEffect(() => {
    // Don't show dropdown when we have 3 tags
    if (searchTags.length >= 3) {
      setShowDropdown(false);
      return;
    }

    const shouldShow =
      isInputFocused &&
      searchExpanded &&
      ((searchValue.length === 0 && recentSearches.length > 0) ||
        (searchValue.length >= 2 && suggestions.length > 0));
    setShowDropdown(shouldShow);
  }, [
    isInputFocused,
    searchExpanded,
    searchValue,
    recentSearches.length,
    suggestions.length,
    searchTags.length,
  ]);

  // Update dropdown position when it should be shown
  useEffect(() => {
    if (showDropdown && searchExpanded) {
      const updatePosition = () => {
        // Use the input container for positioning when expanded
        const targetElement = inputContainerRef.current || containerRef.current;
        if (!targetElement) return;

        const rect = targetElement.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const dropdownWidth = 400; // Increased width significantly for better readability

        // Calculate left position, ensuring dropdown stays within viewport
        let leftPosition = rect.left;

        // If dropdown would go off the right edge, adjust it
        if (leftPosition + dropdownWidth > viewportWidth - 20) {
          leftPosition = viewportWidth - dropdownWidth - 20;
        }

        // If dropdown would go off the left edge, adjust it
        if (leftPosition < 20) {
          leftPosition = 20;
        }

        const newPosition = {
          top: rect.bottom + 4,
          left: leftPosition,
          width: dropdownWidth,
        };
        setDropdownPosition(newPosition);
      };

      // Update position immediately and on scroll/resize
      updatePosition();

      const handleUpdate = () => updatePosition();
      window.addEventListener("scroll", handleUpdate);
      window.addEventListener("resize", handleUpdate);

      return () => {
        window.removeEventListener("scroll", handleUpdate);
        window.removeEventListener("resize", handleUpdate);
      };
    }
  }, [showDropdown, searchExpanded]);

  // Reset selected index when dropdown content changes
  useEffect(() => {
    setSelectedIndex(-1);
  }, [suggestions, recentSearches, searchValue]);

  // Global keyboard handler for 3-tag scenario
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Only handle when we have 3 tags and search is expanded
      if (searchTags.length >= 3 && searchExpanded) {
        if (event.key === "Enter") {
          console.log("🏷️ SearchField: Global Enter key pressed with 3 tags");
          event.preventDefault();
          setShowDropdown(false);
          setIsInputFocused(false);
          navigate("/?tab=All");
        } else if (event.key === "Escape") {
          console.log("🏷️ SearchField: Global Escape key pressed");
          event.preventDefault();
          setSearchExpanded(false);
          setSearchValue("");
          setSearchTags([]);
          setShowDropdown(false);
          setIsInputFocused(false);
        }
      }
    };

    // Add global keydown listener when we have 3 tags
    if (searchTags.length >= 3 && searchExpanded) {
      document.addEventListener("keydown", handleGlobalKeyDown);
      return () => {
        document.removeEventListener("keydown", handleGlobalKeyDown);
      };
    }
  }, [searchTags.length, searchExpanded, navigate, setSearchTags]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking inside the search container or dropdown
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }

      // Close dropdown if clicking outside
      if (showDropdown) {
        setShowDropdown(false);
        setIsInputFocused(false);

        // Only collapse if there are NO tags and no input value
        if (searchTags.length === 0 && !searchValue.trim()) {
          setSearchExpanded(false);
        }
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleDocumentClick);
      return () => {
        document.removeEventListener("mousedown", handleDocumentClick);
      };
    }
  }, [showDropdown, searchTags.length, searchValue]);

  const handleInputFocus = () => {
    setIsInputFocused(true);
    setRecentSearches(searchHistoryService.getSearchHistory());
  };

  const addTag = useCallback(
    (value: string) => {
      // Prevent multiple rapid calls
      if (isAddingTag) return;

      try {
        setIsAddingTag(true);

        const trimmedValue = value.trim();
        console.log(
          "🏷️ SearchField: Adding tag:",
          trimmedValue,
          "Current tags:",
          searchTags,
        );

        if (
          trimmedValue &&
          !searchTags.includes(trimmedValue) &&
          searchTags.length < 3
        ) {
          const newTags = [...searchTags, trimmedValue];
          console.log("🏷️ SearchField: Setting new tags:", newTags);
          setSearchTags(newTags);
          setSearchValue("");

          // Add to search history
          searchHistoryService.addToHistory(trimmedValue);

          // Call onSearch with all tags if provided
          if (onSearch) {
            onSearch(newTags.join(" "));
          }

          // If we just reached 3 tags, close dropdown and unfocus input
          if (newTags.length === 3) {
            // Close dropdown immediately when reaching 3 tags
            setShowDropdown(false);
            setIsInputFocused(false);

            console.log(
              "🏷️ SearchField: Reached 3 tags, global Enter key handler is now active",
            );
          }
        } else if (searchTags.length >= 3) {
          console.log("🏷️ SearchField: Max tags reached, clearing input");
          setSearchValue("");
        }
      } catch (error) {
        console.error("Error in addTag:", error);
      } finally {
        // Reset the flag after processing
        setTimeout(() => setIsAddingTag(false), 50);
      }
    },
    [isAddingTag, searchTags, setSearchTags, onSearch],
  );

  const removeTag = (indexToRemove: number) => {
    const remainingTags = searchTags.filter(
      (_, index) => index !== indexToRemove,
    );
    setSearchTags(remainingTags);

    // Update search with remaining tags
    if (onSearch && remainingTags.length > 0) {
      onSearch(remainingTags.join(" "));
    }

    // Only collapse the search field if this was the last tag AND input is empty
    if (remainingTags.length === 0 && !searchValue.trim()) {
      setSearchExpanded(false);
      setIsInputFocused(false);
      setShowDropdown(false);
    }

    // If there are still tags, keep the search field expanded and focused
    if (remainingTags.length > 0) {
      // Re-focus the input to maintain user interaction (only if we now have < 3 tags)
      setTimeout(() => {
        if (remainingTags.length < 3 && searchInputRef.current) {
          searchInputRef.current.focus();
          setIsInputFocused(true);
        }
      }, 10);
    }
  };

  const handleSearchBlur = (e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as Node;

    // Don't collapse if currently expanding
    if (isExpanding) {
      return;
    }

    // Don't collapse if focus is moving to dropdown or search container
    if (
      containerRef.current?.contains(relatedTarget) ||
      dropdownRef.current?.contains(relatedTarget)
    ) {
      return;
    }

    // Delay to allow for clicks to register
    setTimeout(() => {
      // Only convert current input to tag if it has content AND dropdown is not shown
      // This prevents typed text from becoming a tag when clicking suggestions
      if (searchValue.trim() && !showDropdown) {
        addTag(searchValue);
      }

      // Always close dropdown and set input as not focused when blurring
      setIsInputFocused(false);
      setShowDropdown(false);

      // Only collapse if there are NO tags and no input value
      // This is the key fix - we should keep the search expanded if there are any tags
      if (searchTags.length === 0 && !searchValue.trim()) {
        setSearchExpanded(false);
      }
    }, 150); // Reduced delay slightly for more responsive behavior
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      addTag(searchValue);
    }
  };

  const handleSuggestionClick = (suggestion: SuggestionItem) => {
    // Clear the current search input to prevent it from being added as a tag
    setSearchValue("");
    setShowDropdown(false);
    setIsInputFocused(false);

    // Only collapse the search field if there are NO tags
    // This is the key fix - keep search expanded if tags exist
    if (searchTags.length === 0) {
      setSearchExpanded(false);
    }

    // Navigate to the selected machine or line (without adding as tag)
    if (suggestion.type === "machine" && suggestion.id) {
      // Find the full machine data for navigation state
      const machine = machines.find(
        (m) =>
          m.maschineBK === suggestion.id || m.maschinennummer === suggestion.id,
      );

      if (machine) {
        const machineId = machine.maschinennummer || machine.maschineBK;
        const machineName = machine.machineName || machine.bautyp;
        const lineId = machine.lineId || "";
        const machineType = machine.bautyp;

        navigate(`/machine/${machineId}`, {
          state: {
            machineName,
            lineId,
            machineType,
            factory: machine.factoryName || "1201 (Factory Name)",
          },
        });
      }
    } else if (suggestion.type === "line" && suggestion.id) {
      // Find the full line data for navigation state
      const line = lines.find((l) => l.linieBK === suggestion.id);

      if (line) {
        navigate(`/line/${line.linieBK}`, {
          state: {
            lineName: line.linieName,
            factory: line.factoryName || "1201 (Factory Name)",
            machineCount: line.maschinen?.length?.toString() || "0",
            lineType: "Production Line",
            supervisor: "John Smith",
          },
        });
      }
    }
  };

  const handleRecentSearchClick = useCallback(
    (recentSearch: SearchHistoryItem) => {
      // Prevent any additional calls if already processing
      if (isAddingTag) return;

      // Use React's batch updating with a direct approach
      const trimmedValue = recentSearch.query.trim();
      if (
        trimmedValue &&
        !searchTags.includes(trimmedValue) &&
        searchTags.length < 3
      ) {
        // Direct tag addition without calling addTag to prevent loops
        const newTags = [...searchTags, trimmedValue];
        setSearchTags(newTags);
        setSearchValue("");

        // Keep input focused and refresh recent searches to show dropdown again
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            setIsInputFocused(true);
            setRecentSearches(searchHistoryService.getSearchHistory());
          }
        }, 10);
      }
    },
    [isAddingTag, searchTags, setSearchTags],
  );

  const getDropdownItems = () => {
    if (searchValue.length === 0) {
      return recentSearches.map((item, index) => ({
        type: "recent" as const,
        item,
        index,
      }));
    } else {
      return suggestions.map((item, index) => ({
        type: "suggestion" as const,
        item,
        index,
      }));
    }
  };

  const dropdownItems = getDropdownItems();

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setSearchExpanded(false);
      setSearchValue("");
      setSearchTags([]);
      setShowDropdown(false);
      setIsInputFocused(false);
      setSelectedIndex(-1);
      return;
    }

    if (e.key === "Enter") {
      // Special case: If we have 3 tags and Enter is pressed, directly navigate to search
      if (searchTags.length >= 3) {
        e.preventDefault();
        console.log(
          "🏷️ SearchField: Enter pressed with 3 tags, navigating to search results",
        );
        setShowDropdown(false);
        setIsInputFocused(false);
        navigate("/?tab=All");
        return;
      }

      // If dropdown is open and an item is selected, handle that first
      if (showDropdown && selectedIndex >= 0 && dropdownItems.length > 0) {
        e.preventDefault();
        const selectedItem = dropdownItems[selectedIndex];
        if (selectedItem.type === "recent") {
          handleRecentSearchClick(selectedItem.item as SearchHistoryItem);
        } else {
          handleSuggestionClick(selectedItem.item as SuggestionItem);
        }
        return;
      }

      // If there are search tags OR search input, behave like magnifier icon click
      if (searchTags.length > 0 || searchValue.trim()) {
        e.preventDefault();

        // Add current input as tag if there's input value
        if (searchValue.trim()) {
          addTag(searchValue);
        }

        setShowDropdown(false);
        setIsInputFocused(false);

        // Navigate to home view with "All" tab active to show search results
        navigate("/?tab=All");
        return;
      }
    }

    // Don't prevent other key events when input is disabled (i.e., when we have 3 tags)
    if (searchTags.length >= 3) {
      return;
    }

    if (!showDropdown || dropdownItems.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % dropdownItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev <= 0 ? dropdownItems.length - 1 : prev - 1,
      );
    }
  };

  const renderSuggestionIcon = (type: SuggestionItem["type"]) => {
    const iconClass = "w-5 h-5 flex-shrink-0"; // Increased icon size

    switch (type) {
      case "machine":
        return (
          <svg
            className={`${iconClass} text-blue-500`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <rect
              x="3"
              y="3"
              width="18"
              height="18"
              rx="2"
              ry="2"
              stroke="currentColor"
              strokeWidth="2"
            />
            <circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="2" />
            <path
              d="M21 15.46A4 4 0 0 1 16.54 21"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        );
      case "line":
        return (
          <svg
            className={`${iconClass} text-green-500`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M3 12h18m-9-9v18" stroke="currentColor" strokeWidth="2" />
          </svg>
        );
      default:
        return (
          <svg
            className={`${iconClass} ${themeClasses.textTertiary}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" />
          </svg>
        );
    }
  };

  // Ctrl/⌘ K focuses the search, as in the other Enlixy apps; the copy of
  // this field hidden at the current width has no box and is skipped
  const mod = useModKey();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "k") return;
      const input = searchInputRef.current;
      if (!input || input.disabled || input.getClientRects().length === 0) return;
      e.preventDefault();
      input.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className={cx(searchStyles.container, className)} ref={containerRef}>
      <div
        className={cx(
          searchStyles.wrapper,
          searchExpanded ? searchStyles.expanded : searchStyles.collapsed,
          // Add focus styles when container is focused and has 3 tags
          searchTags.length >= 3
            ? "focus:outline-none focus:ring-2 focus:ring-on-header/50"
            : "",
        )}
        onKeyDown={(e) => {
          // This is now handled by global keydown listener for better reliability
          console.log(
            "🏷️ SearchField: Container keydown event (delegated to global handler)",
            { key: e.key, searchTagsLength: searchTags.length },
          );
        }}
        onFocus={(e) => {
          console.log("🏷️ SearchField: Container focused", e.target);
        }}
        onBlur={(e) => {
          console.log("🏷️ SearchField: Container blurred", e.target);
        }}
        onClick={(e) => {
          console.log(
            "🏷️ SearchField: Container clicked, searchTags.length:",
            searchTags.length,
          );
          // When we have 3 tags, focus the container for Enter key handling
          if (searchTags.length >= 3) {
            e.preventDefault();
            const currentTarget = e.currentTarget as HTMLElement;
            console.log(
              "🏷️ SearchField: About to focus container, tabIndex:",
              currentTarget.tabIndex,
            );
            currentTarget.focus();
            console.log(
              "🏷️ SearchField: Container focused, activeElement:",
              document.activeElement,
            );
          }
        }}
        tabIndex={searchTags.length >= 3 ? 0 : -1} // Make container focusable when input is disabled
        title={searchTags.length >= 3 ? "Press Enter to search" : undefined}
      >
        {/* Search input (always open) */}
        <div
          ref={inputContainerRef}
          className={cx(
            "transition-all duration-300 ease-out",
            searchExpanded
              ? "opacity-100 scale-100 w-full sm:w-80"
              : "opacity-0 scale-95 w-12 pointer-events-none",
          )}
        >
          <form onSubmit={handleSearchSubmit} className="relative">
            {/* Input field with tags inside: the header search pill of
                @digitalisation/enlixy-comp (HeaderSearch) */}
            <div className="relative w-full h-9 pointer-coarse:h-11 overflow-hidden rounded-full bg-on-header/15 text-on-header ring-1 ring-on-header/25 transition-colors hover:bg-on-header/25 focus-within:bg-on-header/25 focus-within:ring-2 focus-within:ring-on-header/70">
              {/* Small magnifier icon inside the input field (left side) */}
              <button
                type="button"
                onClick={() => {
                  // Navigate to home view "All" tab to show search results
                  setShowDropdown(false);
                  setIsInputFocused(false);

                  // Navigate to home page with "All" tab active
                  navigate("/?tab=All");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    // Navigate to home view "All" tab to show search results
                    setShowDropdown(false);
                    setIsInputFocused(false);

                    // Navigate to home page with "All" tab active
                    navigate("/?tab=All");
                  }
                }}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-on-header/90 hover:text-on-header transition-colors z-10"
                aria-label="Search and view results in All tab"
                title="View search results in All tab"
              >
                <Icon name="search" size={15} aria-hidden="true" />
              </button>

              {/* Tags and input container */}
              <div
                className="flex items-center gap-1 py-1 pl-9 pr-3 h-full overflow-x-hidden"
                style={{ maxWidth: "100%" }}
              >
                {/* Tags display inside input */}
                {searchTags.map((tag, index) => (
                  <React.Fragment key={index}>
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap flex-shrink-0 bg-on-header/20 text-on-header ring-1 ring-on-header/30">
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => removeTag(index)}
                        className="text-on-header/75 hover:text-on-header transition-colors"
                        aria-label={`Remove ${tag}`}
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    {/* Show individual AND/OR operator between tags */}
                    {index < searchTags.length - 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          setSearchOperator(
                            index,
                            searchOperators[index] === "AND" ? "OR" : "AND",
                          )
                        }
                        className={cx(
                          "flex-shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold leading-4 transition-colors",
                          // OR filled, AND outlined: they read apart on any header colour
                          searchOperators[index] === "OR"
                            ? "bg-on-header text-header-ink"
                            : "text-on-header ring-1 ring-on-header/50 hover:bg-on-header/15",
                        )}
                        title={`Click to switch to ${searchOperators[index] === "AND" ? "OR" : "AND"}`}
                      >
                        {searchOperators[index] || "AND"}
                      </button>
                    )}
                  </React.Fragment>
                ))}

                {/* Input field */}
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleSearchBlur}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={
                    searchTags.length === 0
                      ? "Search machines, lines..."
                      : searchTags.length >= 3
                        ? ""
                        : "Add more..."
                  }
                  disabled={searchTags.length >= 3}
                  className={cx(
                    "flex-1 min-w-[80px] bg-transparent border-0 outline-none h-full text-[13px] text-on-header placeholder:text-on-header/80",
                    searchTags.length >= 3
                      ? "cursor-not-allowed opacity-50"
                      : "",
                  )}
                />
                {/* Ctrl/⌘ K, as on the package's HeaderSearch */}
                {!isInputFocused && !searchValue && searchTags.length === 0 && (
                  <kbd className="pointer-events-none hidden flex-shrink-0 rounded bg-on-header/20 px-1.5 font-mono text-[10px] leading-4 text-on-header/90 lg:inline">
                    {mod} K
                  </kbd>
                )}
              </div>
            </div>
          </form>

          {/* Dropdown Menu - Using Portal to render outside header */}
          {showDropdown &&
            createPortal(
              <div
                ref={dropdownRef}
                className="scroll-thin fixed z-[9999] max-h-[32rem] overflow-y-auto rounded-2xl bg-surface text-fg shadow-2xl ring-1 ring-line/70"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                  minWidth: "400px",
                }}
              >
                {/* Search Expression Header - only show when there are multiple tags */}
                {searchTags.length > 1 && (
                  <div
                    className={cx(
                      "px-5 py-3 border-b",
                      currentTheme.isDark
                        ? "bg-gray-800/50 border-gray-600/50"
                        : "bg-gray-50/50 border-gray-200/50",
                    )}
                  >
                    <span className={cx("text-sm", themeClasses.textSecondary)}>
                      Search Expression:{" "}
                      <span className="font-mono font-medium">
                        {searchTags.map((tag, index) => (
                          <span key={index}>
                            {tag}
                            {index < searchTags.length - 1 && (
                              <span
                                className="mx-1 font-bold"
                                style={{
                                  color:
                                    searchOperators[index] === "AND"
                                      ? "#3b82f6"
                                      : "#10b981",
                                }}
                              >
                                {searchOperators[index] || "AND"}
                              </span>
                            )}
                          </span>
                        ))}
                      </span>
                    </span>
                  </div>
                )}

                {/* Recent Searches Section */}
                {searchValue.length === 0 && recentSearches.length > 0 && (
                  <>
                    <div
                      className={cx(
                        "px-5 py-4 text-sm font-semibold border-b",
                        currentTheme.isDark
                          ? "text-gray-400 bg-gray-800/50 border-gray-600/50"
                          : "text-gray-500 bg-gray-50/50 border-gray-200/50",
                      )}
                    >
                      Recent Searches
                    </div>
                    {recentSearches.slice(0, 10).map((recentSearch, index) => (
                      <div
                        key={`recent-${index}`}
                        className={`px-5 py-4 cursor-pointer transition-colors ${
                          selectedIndex === index
                            ? `${themeClasses.bgInfoSubtle} ${themeClasses.textInfo}`
                            : `${themeClasses.hover} ${themeClasses.textSecondary}`
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRecentSearchClick(recentSearch);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <div className="flex items-center gap-4">
                          <svg
                            className="w-5 h-5 text-gray-400 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                          <span className="text-base font-medium truncate">
                            {recentSearch.query}
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Suggestions Section */}
                {searchValue.length > 0 && suggestions.length > 0 && (
                  <>
                    <div
                      className={cx(
                        "px-5 py-4 text-sm font-semibold border-b",
                        currentTheme.isDark
                          ? "text-gray-400 bg-gray-800/50 border-gray-600/50"
                          : "text-gray-500 bg-gray-50/50 border-gray-200/50",
                      )}
                    >
                      Suggestions
                    </div>
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={`suggestion-${index}`}
                        className={`px-5 py-4 cursor-pointer transition-colors ${
                          selectedIndex === index
                            ? `${themeClasses.bgInfoSubtle} ${themeClasses.textInfo}`
                            : `${themeClasses.hover} ${themeClasses.textSecondary}`
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSuggestionClick(suggestion);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <div className="flex items-center gap-4">
                          {renderSuggestionIcon(suggestion.type)}
                          <div className="flex-1 min-w-0">
                            <div
                              className={cx(
                                "text-base font-medium truncate",
                                themeClasses.textPrimary,
                              )}
                            >
                              {suggestion.label}
                            </div>
                            {suggestion.subtitle && (
                              <div
                                className={cx(
                                  "text-sm truncate mt-1",
                                  themeClasses.textSecondary,
                                )}
                              >
                                {suggestion.subtitle}
                              </div>
                            )}
                          </div>
                          <div
                            className={cx(
                              "text-sm capitalize px-3 py-1.5 rounded-full font-medium",
                              currentTheme.isDark
                                ? "text-gray-300 bg-gray-700/50"
                                : "text-gray-400 bg-gray-100",
                            )}
                          >
                            {suggestion.type}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* No Results */}
                {searchValue.length > 0 && suggestions.length === 0 && (
                  <div className="px-5 py-6 text-center text-gray-500">
                    <svg
                      className="w-10 h-10 mx-auto mb-3 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        cx="11"
                        cy="11"
                        r="8"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M21 21l-4.35-4.35"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                    <div className="text-base font-medium">
                      No suggestions found
                    </div>
                    <div className="text-sm mt-2">
                      Try searching for machines or lines
                    </div>
                  </div>
                )}
              </div>,
              document.body,
            )}
        </div>
      </div>
    </div>
  );
};

export default SearchField;
